/**
 * Price Service — fetches crypto prices from our server-side /api/prices
 * Server handles Binance (primary) + CoinGecko (fallback) with 60s cache
 */

// Client-side cache to avoid hitting our API on every component re-render
const clientCache: Record<string, { price: number; timestamp: number }> = {}
const CLIENT_CACHE_MS = 30_000 // 30 seconds (server caches for 60s anyway)

// Bulk price cache — stores the full response from /api/prices
let bulkPrices: Record<string, number> | null = null
let bulkTimestamp = 0
const BULK_CACHE_MS = 30_000 // 30 seconds

/**
 * Fetch all prices from server at once (efficient — single request)
 */
async function fetchAllPrices(): Promise<Record<string, number>> {
    const now = Date.now()
    if (bulkPrices && now - bulkTimestamp < BULK_CACHE_MS) {
        return bulkPrices
    }

    try {
        const res = await fetch("/api/prices")
        if (!res.ok) throw new Error("Price API error")
        const data = await res.json()
        if (data.success && data.prices) {
            bulkPrices = data.prices
            bulkTimestamp = Date.now()

            // Also populate individual cache
            for (const [sym, price] of Object.entries(data.prices)) {
                clientCache[sym] = { price: price as number, timestamp: Date.now() }
            }

            return data.prices
        }
    } catch (e) {
        console.error("[PriceService] Failed to fetch prices:", e)
    }

    return bulkPrices || {}
}

/**
 * Get the current USD price for a cryptocurrency
 * @param symbol - The currency symbol (BNB, ETH, SOL, TRX, TON, USDT, etc.)
 * @returns The USD price or null if unavailable
 */
export async function getCryptoPrice(symbol: string): Promise<number | null> {
    const upper = symbol.toUpperCase()

    // Stablecoins — always $1, no fetch needed
    if (["USDT", "USDC", "DAI", "BUSD"].includes(upper)) return 1

    // Check client cache
    const cached = clientCache[upper]
    if (cached && Date.now() - cached.timestamp < CLIENT_CACHE_MS) {
        return cached.price
    }

    // Fetch all prices from server (one request, all symbols)
    const prices = await fetchAllPrices()
    return prices[upper] ?? null
}

/**
 * Get all prices at once (for components that need multiple prices)
 */
export async function getAllCryptoPrices(): Promise<Record<string, number>> {
    return fetchAllPrices()
}

/**
 * Calculate tokens received for a given amount and currency
 */
export function calculateTokensReceived(
    amount: number,
    currency: string,
    tokenPriceUSD: number,
    currencyPriceUSD: number = 1
): number {
    const upper = currency.toUpperCase()
    if (["USDT", "USDC", "DAI", "BUSD"].includes(upper)) {
        currencyPriceUSD = 1
    }
    const usdValue = amount * currencyPriceUSD
    return usdValue / tokenPriceUSD
}

/**
 * Calculate the amount in a currency needed to get a specific number of tokens
 */
export function calculateAmountNeeded(
    tokens: number,
    currency: string,
    tokenPriceUSD: number,
    currencyPriceUSD: number = 1
): number {
    const upper = currency.toUpperCase()
    if (["USDT", "USDC", "DAI", "BUSD"].includes(upper)) {
        currencyPriceUSD = 1
    }
    const usdNeeded = tokens * tokenPriceUSD
    return usdNeeded / currencyPriceUSD
}

/**
 * Format price for display
 */
export function formatPrice(price: number, decimals: number = 2): string {
    if (price >= 1000) {
        return price.toLocaleString(undefined, {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        })
    }
    return price.toFixed(decimals)
}

/**
 * Check if a currency is a stablecoin
 */
export function isStablecoin(symbol: string): boolean {
    return ["USDT", "USDC", "DAI", "BUSD"].includes(symbol.toUpperCase())
}
